package diana.soleil.design.iraniancanadienbazzar.core.data.firestore

import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.BlockRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.NotificationRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.UserPreferences
import diana.soleil.design.iraniancanadienbazzar.core.data.UserProfile
import diana.soleil.design.iraniancanadienbazzar.core.data.UserProfileRepository
import diana.soleil.design.iraniancanadienbazzar.core.firebase.AuthService
import diana.soleil.design.iraniancanadienbazzar.core.firebase.FirebaseServices
import diana.soleil.design.iraniancanadienbazzar.core.model.AuthUser
import diana.soleil.design.iraniancanadienbazzar.core.model.AppNotification
import diana.soleil.design.iraniancanadienbazzar.core.model.BlockedUser
import dev.gitlive.firebase.firestore.Direction
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.Timestamp
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map

/**
 * The signed-in user: profile, block list, notifications + device registrations.
 *
 * Split out of the former 2,753-line FirestoreRepositories.kt (2026-08-02) — same code,
 * same behaviour, one file per domain. Shared collection names and helpers live in
 * FirestoreCommon.kt.
 */

/**
 * Live [UserProfileRepository] over Cloud Firestore (GitLive). Read-then-write: the create path must
 * send the full rule-compliant doc (`role: 'user'`), while later preference writes merge only the two
 * preference fields so everything else on the doc (role, savedBusinessIds, fcmTokens, …) survives.
 */
class FirestoreUserProfileRepository(
    firebase: FirebaseServices,
    private val auth: AuthService,
) : UserProfileRepository {

    private val firestore: FirebaseFirestore = firebase.firestore
    private val functions = firebase.functions

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun observeProfile(): Flow<DataResult<UserProfile?>> =
        auth.authState.flatMapLatest { user ->
            if (user == null || user.isAnonymous) {
                kotlinx.coroutines.flow.flowOf(DataResult.Success(null))
            } else {
                firestore.collection(USERS).document(user.uid).snapshots
                    .map { snap ->
                        val profile = if (snap.exists) snap.decode<UserProfileReadDoc>().toModel() else null
                        DataResult.Success(profile) as DataResult<UserProfile?>
                    }
                    .catch { emit(DataResult.Failure(it.toAppError())) }
            }
        }

    override suspend fun updateProfile(displayName: String, bio: String): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            firestore.collection(USERS).document(user.uid)
                .set(UserProfileUpdateDoc(displayName = displayName, bio = bio), merge = true)
            // Mirror a minimal public identity so other users can discover + DM this person. The private
            // users/{uid} doc stays admin/owner-only; publicProfiles exposes only name (+ lowercase key
            // for prefix search). Best-effort — a mirror failure must not fail the profile save.
            val trimmed = displayName.trim()
            if (trimmed.isNotEmpty()) {
                runCatching {
                    firestore.collection(PUBLIC_PROFILES).document(user.uid).set(
                        PublicProfileWriteDoc(displayName = trimmed, displayNameLower = trimmed.lowercase()),
                        merge = true,
                    )
                }
                // Keep the Auth user's name in step with the profile. Email/password sign-up never
                // sets one, so anything snapshotting `currentUser.displayName` (DM + group threads)
                // stored an empty name and rendered a blank tag. Best-effort, like the mirror above.
                runCatching { auth.updateDisplayName(trimmed) }
            }
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun setGemPushEnabled(enabled: Boolean): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            firestore.collection(USERS).document(user.uid)
                .set(UserGemPushPrefDoc(gemPushEnabled = enabled), merge = true)
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun setPushEnabled(enabled: Boolean): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            firestore.collection(USERS).document(user.uid)
                .set(UserPushPrefDoc(pushEnabled = enabled), merge = true)
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun setEmailMarketingConsent(consented: Boolean): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            firestore.collection(USERS).document(user.uid)
                .set(
                    UserEmailPrefDoc(
                        emailMarketing = EmailMarketingWriteDoc(
                            consented = consented,
                            updatedAt = Timestamp.ServerTimestamp,
                            source = "app-settings",
                        ),
                    ),
                    merge = true,
                )
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun createSubscriptionCheckout(planId: String): DataResult<String> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            val result = functions.httpsCallable("createSubscriptionCheckout")
                .invoke(mapOf("plan" to planId))
            val url = result.data<PromotionCheckoutResult>().url
                ?: return DataResult.Failure(AppError.Unknown())
            DataResult.Success(url)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun createBillingPortalSession(): DataResult<String> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            val result = functions.httpsCallable("createBillingPortalSession").invoke()
            val url = result.data<PromotionCheckoutResult>().url
                ?: return DataResult.Failure(AppError.Unknown())
            DataResult.Success(url)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun getPreferences(uid: String): DataResult<UserPreferences?> = try {
        val snapshot = firestore.collection(USERS).document(uid).get()
        DataResult.Success(if (snapshot.exists) snapshot.decode<UserPrefsReadDoc>().toModel() else null)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override suspend fun getRole(uid: String): DataResult<String?> = try {
        val snapshot = firestore.collection(USERS).document(uid).get()
        DataResult.Success(if (snapshot.exists) snapshot.decode<UserRoleDoc>().role else null)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override suspend fun syncPreferences(
        user: AuthUser,
        preferredLanguage: String,
        metroId: String?,
        savedBusinessIds: Set<String>,
        savedNewsIds: Set<String>,
        interests: Set<String>,
    ): DataResult<Unit> = try {
        val doc = firestore.collection(USERS).document(user.uid)
        if (doc.get().exists) {
            doc.set(
                UserPrefsDoc(
                    preferredLanguage = preferredLanguage,
                    metro = metroId,
                    savedBusinessIds = savedBusinessIds.sorted(),
                    savedNewsIds = savedNewsIds.sorted(),
                    interests = interests.sorted(),
                ),
                merge = true,
            )
        } else {
            doc.set(
                UserCreateDoc(
                    role = ROLE_USER,
                    displayName = user.displayName,
                    preferredLanguage = preferredLanguage,
                    metro = metroId,
                    savedBusinessIds = savedBusinessIds.sorted(),
                    savedNewsIds = savedNewsIds.sorted(),
                    interests = interests.sorted(),
                    createdAt = Timestamp.ServerTimestamp,
                ),
            )
        }
        // Self-heal accounts created before 2026-09-17: email/password sign-up never set an Auth
        // display name, so anything snapshotting it wrote an empty name (blank tags in Messages).
        // This runs on the preferences sync that follows every sign-in, so an affected member is
        // repaired the next time they open the app — no backfill needed for the source of truth.
        if (user.displayName.isNullOrBlank()) {
            runCatching {
                val saved = firestore.collection(USERS).document(user.uid).get()
                    .takeIf { it.exists }
                    ?.get<String?>("displayName")
                    ?.trim()
                if (!saved.isNullOrEmpty()) auth.updateDisplayName(saved)
            }
        }
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }
}

/** Private per-user block list over `users/{uid}/blockedUsers/{targetUid}`. */
class FirestoreBlockRepository(
    firebase: FirebaseServices,
    private val auth: AuthService,
) : BlockRepository {

    private val firestore: FirebaseFirestore = firebase.firestore

    private fun blocked(uid: String) =
        firestore.collection(USERS).document(uid).collection(BLOCKED_USERS)

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun observeBlockedUids(): Flow<Set<String>> =
        auth.authState.flatMapLatest { user ->
            if (user == null || user.isAnonymous) {
                kotlinx.coroutines.flow.flowOf(emptySet())
            } else {
                blocked(user.uid).snapshots
                    .map { snap -> snap.documents.map { it.id }.toSet() }
                    .catch { emit(emptySet()) }
            }
        }

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun observeBlockedUsers(): Flow<List<BlockedUser>> =
        auth.authState.flatMapLatest { user ->
            if (user == null || user.isAnonymous) {
                kotlinx.coroutines.flow.flowOf(emptyList())
            } else {
                blocked(user.uid).snapshots
                    .map { snap ->
                        snap.documents.mapNotNull { doc ->
                            runCatching { BlockedUser(doc.id, doc.decode<BlockedUserDoc>().name) }.getOrNull()
                        }
                    }
                    .catch { emit(emptyList()) }
            }
        }

    override suspend fun block(targetUid: String, targetName: String): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        if (targetUid == user.uid) return DataResult.Failure(AppError.Unknown())
        return try {
            blocked(user.uid).document(targetUid)
                .set(BlockedUserDoc(name = targetName, createdAt = Timestamp.ServerTimestamp))
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun unblock(targetUid: String): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            blocked(user.uid).document(targetUid).delete()
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }
}

/** User-scoped in-app notifications and FCM device registrations. */
class FirestoreNotificationRepository(
    firebase: FirebaseServices,
    private val auth: AuthService,
) : NotificationRepository {
    private val firestore: FirebaseFirestore = firebase.firestore

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun observeNotifications(): Flow<DataResult<List<AppNotification>>> =
        auth.authState.flatMapLatest { user ->
            if (user == null || user.isAnonymous) {
                kotlinx.coroutines.flow.flowOf(DataResult.Failure(AppError.Unauthorized()))
            } else {
                userNotifications(user.uid)
                    .orderBy("createdAt", Direction.DESCENDING).limit(50)
                    .snapshots
                    .map { snap ->
                        val notifications = snap.documents
                            .mapNotNull { doc ->
                                runCatching { doc.decode<NotificationDoc>().toModel(doc.id) }.getOrNull()
                            }
                            .sortedByDescending { it.createdAtMillis ?: 0L }
                        DataResult.Success(notifications) as DataResult<List<AppNotification>>
                    }
                    .catch { emit(DataResult.Failure(it.toAppError())) }
            }
        }

    override suspend fun markConversationRead(conversationId: String): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            // Server-side unread filter like markAllRead — equality-only filters merge single-field
            // indexes, so no composite index is needed and read docs never come down the wire.
            val unread = userNotifications(user.uid)
                .where { "conversationId" equalTo conversationId }
                .where { "read" equalTo false }
                .get()
                .documents
            unread.forEach { it.reference.updateFields { "read" to true } }
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun markRead(notificationId: String): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            userNotifications(user.uid).document(notificationId).updateFields { "read" to true }
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun markAllRead(): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            // Server-side filter: only unread docs come down (automatic single-field index) —
            // fetching the whole subcollection to flip a few flags pays reads for nothing.
            val unread = userNotifications(user.uid).where { "read" equalTo false }.get().documents
            unread.forEach { it.reference.updateFields { "read" to true } }
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun registerDevice(token: String, language: String?): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        if (token.isBlank()) return DataResult.Failure(AppError.Unknown())
        return try {
            firestore.collection(USERS).document(user.uid).collection(DEVICES).document(token).set(
                DeviceRegistrationDoc(
                    token = token,
                    updatedAt = Timestamp.ServerTimestamp,
                    // Rules accept only en/fr/fa; anything else is dropped rather than failing the write.
                    language = language?.takeIf { it in setOf("en", "fr", "fa") },
                ),
            )
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    private fun userNotifications(uid: String) =
        firestore.collection(USERS).document(uid).collection(NOTIFICATIONS)
}
